(() => {
  const cleanups = new WeakMap();

  const register = (root, fn) => {
    const list = cleanups.get(root) || [];
    list.push(fn);
    cleanups.set(root, list);
  };

  const reveal = { opacity: 0, y: 40, visibility: "hidden" };

  /* ---------------------------------------------------------------
     Headings — words rise inside a per-line clip-path mask
     --------------------------------------------------------------- */
  const splitHeading = (el) => {
    const split = new SplitText(el, {
      type: "lines,words",
      linesClass: "nad-line",
      wordsClass: "nad-word",
    });

    // clip-path with a negative inset lets descenders and tall glyphs breathe
    // sideways while still cutting off the vertical travel.
    gsap.set(split.lines, {
      clipPath: "inset(-1rem)",
      display: "block",
    });

    gsap.set(el, { visibility: "visible" });

    return split;
  };

  const initHeadings = (root) => {
    root.querySelectorAll("[data-h-animation]").forEach((el) => {
      const split = splitHeading(el);

      const tween = gsap.from(split.words, {
        yPercent: 150,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.03,
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
        },
      });

      register(root, () => {
        tween.scrollTrigger?.kill();
        tween.kill();
        split.revert();
      });
    });
  };

  /* ---------------------------------------------------------------
     Slides — a single element fading up
     --------------------------------------------------------------- */
  const initSlides = (root) => {
    root.querySelectorAll("[data-slide-animation]").forEach((el) => {
      const endOpacity = Number(gsap.getProperty(el, "opacity")) || 1;

      const tween = gsap.fromTo(
        el,
        reveal,
        {
          opacity: endOpacity,
          y: 0,
          visibility: "visible",
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 88%",
          },
        }
      );

      register(root, () => {
        tween.scrollTrigger?.kill();
        tween.revert();
      });
    });
  };

  /* ---------------------------------------------------------------
     Grids — direct children stagger in
     --------------------------------------------------------------- */
  const initGrids = (root) => {
    root.querySelectorAll("[data-grid-animation]").forEach((el) => {
      const items = el.children;
      if (!items.length) return;

      gsap.set(el, { visibility: "visible" });

      const tween = gsap.fromTo(
        items,
        { opacity: 0, y: 32, visibility: "hidden" },
        {
          opacity: 1,
          y: 0,
          visibility: "visible",
          duration: 0.7,
          ease: "power2.out",
          stagger: 0.09,
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
          },
        }
      );

      register(root, () => {
        tween.scrollTrigger?.kill();
        tween.kill();
      });
    });
  };

  /* ---------------------------------------------------------------
     Hero — plays on load, not on scroll: it is already in view
     --------------------------------------------------------------- */
  const initHero = (root) => {
    const heading = root.querySelector("[data-hero-element='heading']");
    const slides = root.querySelectorAll("[data-hero-element='slide']");
    if (!heading && !slides.length) return;

    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    let split = null;

    if (heading) {
      split = splitHeading(heading);

      tl.from(split.words, {
        yPercent: 150,
        duration: 1,
        stagger: 0.04,
      });
    }

    if (slides.length) {
      tl.fromTo(
        slides,
        { opacity: 0, y: 30, visibility: "hidden" },
        {
          opacity: 1,
          y: 0,
          visibility: "visible",
          duration: 0.6,
          stagger: 0.08,
        },
        heading ? "-=0.4" : 0
      );
    }

    register(root, () => {
      tl.kill();
      split?.revert();
    });
  };

  const initAll = (root) => {
    initHero(root);
    initHeadings(root);
    initSlides(root);
    initGrids(root);
  };

  const destroyAll = (root) => {
    (cleanups.get(root) || []).forEach((fn) => fn());
    cleanups.delete(root);
  };

  const mm = gsap.matchMedia();

  mm.add("(prefers-reduced-motion: no-preference)", () => {
    const start = () => {
      initAll(document);
      ScrollTrigger.refresh();
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", start);
    } else {
      start();
    }

    // Theme editor re-renders sections, so animations are rebuilt per section.
    const onLoad = (event) => initAll(event.target);
    const onUnload = (event) => destroyAll(event.target);

    document.addEventListener("shopify:section:load", onLoad);
    document.addEventListener("shopify:section:unload", onUnload);

    // Images and webfonts change layout height after they land, which shifts
    // every trigger position computed before that.
    const onWindowLoad = () => ScrollTrigger.refresh();
    window.addEventListener("load", onWindowLoad);
    document.fonts.ready.then(() => ScrollTrigger.refresh());

    return () => {
      document.removeEventListener("DOMContentLoaded", start);
      document.removeEventListener("shopify:section:load", onLoad);
      document.removeEventListener("shopify:section:unload", onUnload);
      window.removeEventListener("load", onWindowLoad);
      destroyAll(document);
    };
  });

  mm.add("(prefers-reduced-motion: reduce)", () => {
    gsap.set(
      "[data-hero-element], [data-slide-animation], [data-h-animation], [data-grid-animation], [data-grid-animation] > *",
      { visibility: "visible", opacity: 1 }
    );
  });
})();